import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx-ugnis';
import { useCallback, useState } from 'react';
import { useRsi } from '../hooks/useRsi';
import Button from 'common/components/button/Button';
import { useTranslation } from 'react-i18next';
import styles from './DropZone.module.scss';
import { useToasts } from 'react-toast-notifications';

type DropZoneProps = {
  onContinue: (data: XLSX.WorkBook, file: File) => void;
  isLoading: boolean;
};

export const readFileAsync = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsArrayBuffer(file);
  });
};

export const DropZone = ({ onContinue, isLoading }: DropZoneProps) => {
  const { maxFileSize, dateFormat, parseRaw } = useRsi();  
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const errorToast = useCallback( (description: string) => { addToast(description, { appearance: 'error' })}, []);  
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    maxSize: maxFileSize,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDropRejected: fileRejections => {
      setLoading(false);
      fileRejections.forEach(fileRejection => {
        errorToast(`${fileRejection.file.name} ${t('spreadsheet_import.upload_step.dropzone.error_toast_description')}: ${fileRejection.errors[0].message}`)      
      });
    },
    onDropAccepted: async ([file]) => {
      setLoading(true);
      const arrayBuffer = await readFileAsync(file);
      const workbook = XLSX.read(arrayBuffer, {
        cellDates: true,
        dateNF: dateFormat,
        raw: parseRaw,
        dense: true,
      });
      setLoading(false);     
      onContinue(workbook, file);
    },
  });

  return (
    <div
      {...getRootProps()}
      className={styles.container}
    >
      <input {...getInputProps()} data-testid="rsi-dropzone" />
      {isDragActive ? (
        <p>{ t('spreadsheet_import.upload_step.dropzone.active_dropzone_title') }</p>
      ) : loading || isLoading ? (
        <p>{ t('spreadsheet_import.upload_step.dropzone.loading_title')}</p>
      ) : (
        <>
          <p>{t('spreadsheet_import.upload_step.dropzone.title')}</p>
          <Button onClick={open}>
            {t('spreadsheet_import.upload_step.dropzone.button_title')}
          </Button>
        </>
      )}
    </div>
  );
};
